<template>
  <v-container fluid>
    <NewUserDialog
      v-if="isUserProfilePage && showUserPermission.dialog"
      :showUserPermission="showUserPermission"
      isProfilePage
      data-cc="user-permissions-dialog"
      @close="toggleUserPermissionsDialog()"
      @userUpdated="$emit('userUpdated')"
    />

    <v-row>
      <v-col cols="12" class="text-h5 d-flex align-center">
        {{ $t('generic.permissions') }}

        <v-spacer />
        <v-icon-btn
          v-if="isUserProfilePage && isAdmin"
          variant="text"
          icon="$edit"
          color="primary"
          data-cc="user-permissions-edit-icon"
          @click="toggleUserPermissionsDialog(true)"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-data-table
          class="user-permissions-table-row"
          :headers="userPermissionsTableHeaders"
          :items="mappedPermissions"
          :sortBy="[{ key: 'role', order: 'asc' }]"
          hideDefaultFooter
          mustSort
          data-cc="user-permissions-table"
        >
          <template #item="{ item }">
            <tr data-cc="user-permissions-table-row">
              <td data-cc="user-permissions-table-cell-role">
                {{ item.role }}
              </td>

              <td
                v-sanitize="item.scopeDetails.join('<br />')"
                data-cc="user-permissions-table-cell-scope"
              />

              <td data-cc="user-permissions-table-cell-scope">
                <v-icon-btn
                  icon="mdi-abacus"
                  variant="text"
                  color="secondary"
                  data-cc="user-permissions-table-cell-action"
                  @click.stop="selectedItem = item"
                />
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <BMDialog
      v-if="selectedItem"
      :showDialog="Object.keys(selectedItem).length > 0"
      :persistent="false"
      :headerText="
        $t('userProfile.settings.permissionsDialog.header.userActions')
      "
      dialogTestId="user-permissions-actions-dialog"
      hideSubmitButton
      largeDialog
      @closed="selectedItem = null"
    >
      <BMTable
        class="user-actions-table"
        :tableFilters="userActionsTableFilters"
        :headers="userActionsTableHeaders"
        :search="search"
        :items="selectedItem.actions"
        tableHeight="100%"
        data-cc="user-permissions-action-table"
        @clearFilters="search = ''"
      />
    </BMDialog>
  </v-container>
</template>

<script>
import NewUserDialog from '@/components/common/user/NewUserDialog';
import BMTable from '@/components/common/utilities/table/BMTable';
import BMSearch from '@/components/common/utilities/table/BMSearch.vue';
import BMDialog from '@/components/common/utilities/BMDialog.vue';
import { useTableFilters } from '@/composables/useTableFilters';
import { usePermissions } from '@/composables/usePermissions';

export default {
  name: 'UserPermissionComponent',
  components: {
    BMDialog,
    BMTable,
    NewUserDialog
  },
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  setup() {
    const { search, searchChanged } = useTableFilters();
    const { isAdmin } = usePermissions();

    return { search, searchChanged, isAdmin };
  },
  data() {
    return {
      showUserPermission: {
        dialog: false,
        user: {}
      },
      selectedItem: null
    };
  },
  computed: {
    isUserProfilePage() {
      return this.$route.params.hasOwnProperty('userHashId');
    },
    userActionsTableFilters() {
      return [
        {
          component: BMSearch,
          props: {
            modelValue: this.search,
            name: 'user-permission',
            'data-cc': 'user-permissions-action-table-search-filter'
          },
          events: {
            'update:modelValue': this.searchChanged
          }
        }
      ];
    },
    userActionsTableHeaders() {
      return [
        {
          title: this.$t('roles.actionsString'),
          key: 'action'
        },
        {
          title: this.$t('generic.description'),
          key: 'description'
        }
      ];
    },
    userPermissionsTableHeaders() {
      return [
        {
          title: this.$t('userProfile.role'),
          key: 'role',
          headerProps: { class: 'text-table-header' }
        },
        {
          title: this.$t('userProfile.scope'),
          key: 'scopeDetails',
          headerProps: { class: 'text-table-header' }
        },
        {
          title: this.$t('roles.actionsString'),
          key: 'actions',
          headerProps: { class: 'text-table-header' }
        }
      ];
    },
    mappedPermissions() {
      return this.user.permissionsV2.reduce((accumulator, permission) => {
        const existingPermission = accumulator.find(
          (p) => p.roleId === permission.role.id
        );

        if (existingPermission) {
          existingPermission.scopeDetails.push(
            permission.scopeDetails?.name || permission.scope
          );
        } else {
          const newPermission = {
            roleId: permission.role.id,
            role: permission.role.name,
            scopeDetails: [permission.scopeDetails?.name || permission.scope],
            actions: permission.role.actions.map((action) => ({
              action: this.$t(`roles.actions.name.${action.localizedName}`),
              description: this.$t(
                `roles.actions.description.${action.localizedName}`
              )
            }))
          };
          accumulator.push(newPermission);
        }

        return accumulator;
      }, []);
    }
  },
  methods: {
    toggleUserPermissionsDialog(open = false) {
      this.showUserPermission = {
        dialog: open,
        user: open ? this.user : {}
      };
    }
  }
};
</script>
